// Libs
import React, { useState, useContext } from "react";
import { StoresContext, Stores } from "Globals/Stores";
import { useObserver, KeyValuePair, Grid, Else, FieldType, IViewModel } from "@shoothill/core";
import { Link as RouterLink } from "react-router-dom";
import { RadioSVG } from "Globals/Views/IconSVGs/RadioSVG";
import { RadioCheckedSVG } from "Globals/Views/IconSVGs/RadioCheckedSVG";
import { Button, Table, TableHead, TableBody, TableRow, TableContainer, TableCell, Box, Radio, FormControl, FormHelperText, Select, Typography } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import styled from "styled-components";
import { BinSVG } from "Globals/Views/IconSVGs/BinSVG";
import { CustomDatePicker } from "Globals/Views/CustomDatePicker";

// App
import { theme } from "Globals/Styles/AppTheme";
import PropertyDetailsViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PropertyDetailsViewModel";
import PersonViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PersonViewModel";
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import { CurrencyInput } from "Globals/Views/CurrencyInput";

// Styles
import { StyledButton } from "Globals/Styles/ButtonStyles";
import { Row } from "Globals/Styles/LayoutStyles";
import { CustomInput } from "Globals/Styles/AppStyling";

export const StyledAddButton = styled(StyledButton)`
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 0px;
    margin: 0 0 1px 0;
`;
interface IProps {
    propertyDetailsViewModel: PropertyDetailsViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}
export const PersonView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [upsertViewModel] = useState(() => new PersonViewModel());
    return useObserver(() => (
        <>
            <Box style={{ backgroundColor: "#F0F0F0", padding: "15px" }}>
                {props.applicationFormViewModel.model.isSubmitted == false && (
                    <>
                        <Row style={{ alignItems: "center" }}>
                            <CustomInput
                                label="Name"
                                placeHolder="Enter their name"
                                viewModel={upsertViewModel}
                                fieldName="name"
                                shrink={true}
                                inputProps={{
                                    disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                                }}
                            />
                            <CustomInput
                                type="select"
                                style={{ minWidth: "350px" }}
                                label="Gender"
                                placeHolder="Select their gender"
                                viewModel={upsertViewModel}
                                fieldName="gender"
                                selectItems={storesContext.Domain.SelectItemsStore.getGenderOptions}
                                inputProps={{
                                    disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                                }}
                            />
                            <CustomDatePicker
                                viewModel={upsertViewModel}
                                label="Date of birth"
                                placeholder={"Enter date of birth"}
                                fieldName="dateOfBirth"
                                maxDate={new Date()}
                                value={upsertViewModel.model.dateOfBirth}
                                onChange={(date) => {
                                    props.propertyDetailsViewModel.restartTimer();
                                    upsertViewModel.setDate("dateOfBirth", date);
                                }}
                                resetCallback={props.propertyDetailsViewModel.restartTimer}
                                disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                            />
                            <CustomInput
                                type="select"
                                style={{ minWidth: "350px" }}
                                placeHolder={"Please select"}
                                label="Relationship to you"
                                viewModel={upsertViewModel}
                                fieldName="relationshipTypeId"
                                selectItems={storesContext.Domain.RelationshipTypeStore.getRelationshipTypeOptions}
                                inputProps={{
                                    disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                                }}
                            />
                        </Row>
                        <Row style={{ alignItems: "center" }}>
                            <CustomInput
                                multiLine={true}
                                fullwidth={true}
                                label="Please confirm why this person is not included in the purchase of the property"
                                placeHolder="Reasoning..."
                                viewModel={upsertViewModel}
                                fieldName="notes"
                                inputProps={{
                                    disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                                }}
                            />
                            <Box style={{ display: "flex", alignItems: "start", marginTop: "22px" }}>
                                <StyledAddButton
                                    variant="contained"
                                    disableElevation
                                    onClick={() => upsertViewModel.stash(props.propertyDetailsViewModel)}
                                    disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                                >
                                    +
                                </StyledAddButton>
                            </Box>
                        </Row>
                    </>
                )}
                {props.propertyDetailsViewModel.getSortedPersonViewModels.length > 0 && (
                    <TableContainer>
                        <Table style={{ width: "100%" }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell align="center">Gender</TableCell>
                                    <TableCell align="center">Date of birth</TableCell>
                                    <TableCell align="center">Relationship to you</TableCell>
                                    <TableCell align="left">Why this person is not included in the purchase of the property</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {props.propertyDetailsViewModel.getSortedPersonViewModels.map((person) => (
                                    <TableRow key={person.model.id}>
                                        <TableCell component="th" scope="person">
                                            {person.model.name}
                                        </TableCell>
                                        <TableCell align="center">{storesContext.Domain.SelectItemsStore.getGenderName(person.model.gender)}</TableCell>
                                        <TableCell align="center">{person.getFormattedDateOfBirth}</TableCell>
                                        <TableCell align="center">{storesContext.Domain.RelationshipTypeStore.getRelationshipTypeName(person.model.relationshipTypeId)}</TableCell>
                                        <TableCell align="left">{person.model.notes}</TableCell>
                                        <TableCell align="center">
                                            {(upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving) ==
                                                false && (
                                                <Button
                                                    onClick={() => {
                                                        person.delete(props.propertyDetailsViewModel);
                                                    }}
                                                >
                                                    <BinSVG />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </>
    ));
};
